import { Box, Typography } from '@material-ui/core'
import styled from '@emotion/styled'
import MessageInput from 'src/components/chatbot/components/MessageInput'
import BotMessage from 'src/components/chatbot/components/BotMessage'
import BotLoadingMessage from 'src/components/chatbot/components/BotLoadingMessage'
import ChatEmptyState from 'src/components/chatbot/components/ChatEmptyState'
import QuickAccessBar from 'src/components/chatbot/components/QuickAccessBar'
import ChatLoadingState from 'src/components/chatbot/components/ChatLoadingState'
import UserMessageWithChips from 'src/components/chatbot/components/UserMessageWithChips'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import { useRef, useEffect, useState } from 'react'

const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

const MessagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
`

const Message = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: 12px;
  max-width: 80%;
  word-wrap: break-word;
`

const UserMessage = styled(Message)`
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  align-self: flex-end;
`

const FooterInputContainer = styled.div`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: ${({ theme }) => theme.palette.background.paper};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`

const MessagePair = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`

export default function ChatMessageArea({
  isDarkMode,
  isVariantsTablePage,
  suggestedQueries,
  onHPOButtonClick,
  onDiseaseButtonClick,
  onSamplesButtonClick,
  onPanelsButtonClick,
  selectedHPOTerms,
  onRemoveHPOTerm,
  selectedDiseases,
  onRemoveDisease,
  selectedSamples,
  onRemoveSample,
  selectedPanels,
  onRemovePanel,
}) {
  const { chatMessageHistory, isFirstMessageLoading, messagesContainerRef } = useChatbotStore()

  const [userMessageCount, setUserMessageCount] = useState(0)
  const [botAnswerCount, setBotAnswerCount] = useState(0)

  // Count user messages
  useEffect(() => {
    const count = chatMessageHistory.filter((pair) => pair.userMessage).length
    setUserMessageCount(count)
  }, [chatMessageHistory])

  // Count bot answers (not loading)
  useEffect(() => {
    const count = chatMessageHistory.filter((pair) => pair.botMessage).length
    setBotAnswerCount(count)
  }, [chatMessageHistory])

  const isWaitingForBotAnswer = userMessageCount > botAnswerCount

  // Show loading state when first message is being sent
  if (isFirstMessageLoading && chatMessageHistory.length === 0) {
    return <ChatLoadingState />
  }

  return (
    <ChatBody>
      {chatMessageHistory.length === 0 ? (
        <ChatEmptyState
          isDarkMode={isDarkMode}
          selectedHPOTerms={selectedHPOTerms}
          onRemoveHPOTerm={onRemoveHPOTerm}
          selectedDiseases={selectedDiseases}
          onRemoveDisease={onRemoveDisease}
          selectedSamples={selectedSamples}
          onRemoveSample={onRemoveSample}
          selectedPanels={selectedPanels}
          onRemovePanel={onRemovePanel}
          isVariantsTablePage={isVariantsTablePage}
          suggestedQueries={suggestedQueries}
          onHPOButtonClick={onHPOButtonClick}
          onDiseaseButtonClick={onDiseaseButtonClick}
          onSamplesButtonClick={onSamplesButtonClick}
          onPanelsButtonClick={onPanelsButtonClick}
        />
      ) : (
        <>
          <MessagesContainer ref={messagesContainerRef}>
            {chatMessageHistory.map((pair, index) => (
              <MessagePair key={index}>
                {/* User Message */}
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <UserMessageWithChips userMessage={pair.userMessage} />
                </Box>

                {/* Bot Message or Loading */}
                <Box display="flex" flexDirection="column" alignItems="flex-start">
                  {pair.isLoading ? (
                    <BotLoadingMessage isDarkMode={isDarkMode} />
                  ) : (
                    <BotMessage
                      botMessage={pair.botMessage}
                      isDarkMode={isDarkMode}
                      isLastMessage={index === chatMessageHistory.length - 1}
                    />
                  )}
                </Box>
              </MessagePair>
            ))}
          </MessagesContainer>
          <FooterInputContainer isDarkMode={isDarkMode}>
            <QuickAccessBar
              isDarkMode={isDarkMode}
              onHPOButtonClick={onHPOButtonClick}
              onDiseaseButtonClick={onDiseaseButtonClick}
              onSamplesButtonClick={onSamplesButtonClick}
              onPanelsButtonClick={onPanelsButtonClick}
            />
            <MessageInput
              selectedHPOTerms={selectedHPOTerms}
              onRemoveHPOTerm={onRemoveHPOTerm}
              selectedDiseases={selectedDiseases}
              onRemoveDisease={onRemoveDisease}
              selectedSamples={selectedSamples}
              onRemoveSample={onRemoveSample}
              selectedPanels={selectedPanels}
              onRemovePanel={onRemovePanel}
              isWaitingForBotAnswer={isWaitingForBotAnswer}
            />
          </FooterInputContainer>
        </>
      )}
    </ChatBody>
  )
}
